/**
 * Turns a chatbot message string into a list of text / URL segments suitable
 * for safe React rendering. Lets the widget drop `dangerouslySetInnerHTML`
 * (which would otherwise inject untrusted LLM and user text as HTML) and
 * still render clickable links.
 *
 * Two link syntaxes are recognized:
 *   - Markdown links `[label](href)` — the preferred form the prompt asks the
 *     model to emit, so a link renders as descriptive text instead of a raw URL.
 *   - Bare `http(s)://…` URLs — a fallback so older/plain replies still linkify.
 */

/** Matches http(s) URLs greedily up to the next whitespace boundary. */
const URL_PATTERN = /(https?:\/\/\S+)/g;

/**
 * Matches a Markdown link `[label](href)`. `label` is any run without `]`;
 * `href` is a non-greedy run without whitespace or `)` so it stops at the
 * closing paren. Global so a single line can carry several links.
 */
const MARKDOWN_LINK_PATTERN = /\[([^\]]+)\]\(([^\s)]+)\)/g;

/**
 * Trailing characters that are usually sentence punctuation, not part of the
 * URL itself. Stripping them keeps "visit https://x.com." from linking the
 * trailing period.
 */
const TRAILING_PUNCTUATION = /[.,;:!?)\]]+$/;

export interface MessageSegment {
  kind: "text" | "url";
  /** For a `url` segment this is the href; for `text` it is the literal text. */
  value: string;
  /**
   * Visible text for a `url` segment when it differs from the href (i.e. it
   * came from a Markdown link). Absent for bare URLs, where the href is shown
   * verbatim.
   */
  label?: string;
}

/**
 * Whether an href from a Markdown link is safe to render into an `<a href>`.
 * Absolute http(s) URLs and site-relative paths (`/…`) are allowed; anything
 * else (javascript:, mailto oddities, malformed) falls back to plain text so a
 * dangerous scheme never reaches the DOM.
 */
function isSafeHref(href: string): boolean {
  return href.startsWith("https://") || href.startsWith("http://") || href.startsWith("/");
}

/** Appends `text`, linkifying any bare http(s) URLs inside it, to `segments`. */
function pushTextWithBareUrls(segments: MessageSegment[], text: string): void {
  if (!text) return;
  // Capturing group means split() interleaves text and matches: even
  // indices are text, odd indices are URLs.
  const parts = text.split(URL_PATTERN);
  parts.forEach((part, i) => {
    if (!part) return;
    if (i % 2 === 0) {
      segments.push({ kind: "text", value: part });
      return;
    }
    const trailingMatch = part.match(TRAILING_PUNCTUATION);
    if (trailingMatch) {
      const cut = part.length - trailingMatch[0].length;
      if (cut > 0) segments.push({ kind: "url", value: part.slice(0, cut) });
      segments.push({ kind: "text", value: trailingMatch[0] });
    } else {
      segments.push({ kind: "url", value: part });
    }
  });
}

/**
 * Splits a single line into ordered text + url segments. Markdown links are
 * consumed first (so their href never gets re-scanned as bare-URL text); the
 * gaps between them are then scanned for bare URLs.
 */
function splitLine(line: string): MessageSegment[] {
  const segments: MessageSegment[] = [];
  // Fresh regex so lastIndex never leaks across calls.
  const linkPattern = new RegExp(MARKDOWN_LINK_PATTERN);
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = linkPattern.exec(line)) !== null) {
    const [full, label, href] = match;
    if (match.index > lastIndex) pushTextWithBareUrls(segments, line.slice(lastIndex, match.index));
    if (isSafeHref(href)) {
      segments.push({ kind: "url", value: href, label });
    } else {
      // Unsafe/unknown href: keep the human-readable label, drop the link.
      pushTextWithBareUrls(segments, label);
    }
    lastIndex = match.index + full.length;
  }
  if (lastIndex < line.length) pushTextWithBareUrls(segments, line.slice(lastIndex));
  // An empty line yields no segments (renderer treats it as blank spacing); the
  // top-level empty-input case is handled in linkifyMessage.
  return segments;
}

/**
 * Parses a chatbot message into rows of segments. Each row corresponds to a
 * line of input; the renderer inserts a `<br />` between rows. Empty input
 * returns a single empty-text row so callers can still render a placeholder.
 *
 * @param content - the raw message string (may include `\n` line breaks)
 * @returns an array of rows, each an ordered list of text/url segments
 */
export function linkifyMessage(content: string): MessageSegment[][] {
  if (!content) return [[{ kind: "text", value: "" }]];
  return content.split("\n").map((line) => splitLine(line));
}
